"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { z } from "zod";
import { toast } from "sonner";
import { usePathname } from "@utils/hook/index.hook";
import { MatchesType } from "../../column/index.column";
import CricketFlag from "../../../_components/CricketFlag";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CricketMatchFetch, CricketMatchUpdate } from "@services/CricketMatch";

export const updateWinnersSchema = z.object({
  toss_winner: z.string().min(1, "Please select a toss winner"),
  match_winner: z.string().min(1, "Please select a match winner"),
  player_of_the_match: z.string().min(1, "Please select a player of match"),
});

type UpdateWinnersFormData = z.infer<typeof updateWinnersSchema>;

interface UpdateWinnersFormProps {
  formdata: MatchesType;
  setOpen?: (e: boolean) => void;
}

export function UpdateWinnersForm({
  formdata,
  setOpen,
}: UpdateWinnersFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const pathname = usePathname();

  const form = useForm<UpdateWinnersFormData>({
    resolver: zodResolver(updateWinnersSchema),
    values: {
      toss_winner: formdata?.toss_winner?.id?.toString() || "", //take id from the api response and convert it to string
      match_winner: formdata?.match_winner?.id?.toString() || "", //first modal opens, formdata may be empty
      player_of_the_match: formdata?.player_of_the_match?.id?.toString() || "",
    },
  });

  const { data, error } = useQuery({
    queryKey: ["cricket-match-fetch"],
    queryFn: () => CricketMatchFetch(),
  });

  const players = data?.results?.flatMap((team: any) => team.players) || [];

  const { mutate: MatchMutate } = useMutation({
    mutationFn: (data: any) => CricketMatchUpdate(formdata.id, data),
    onSuccess: () => {
      toast.success("Match added successfully!");
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      if (setOpen) setOpen(false);
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const handleSubmit = async (data: UpdateWinnersFormData) => {
    setIsLoading(true);
    const payload = {
      toss_winner: Number(data.toss_winner), //html select dropdown value is string by default for id, server expects number
      match_winner: Number(data.match_winner),
      player_of_the_match: Number(data.player_of_the_match),
    };
    MatchMutate(payload);
  };

  console.log("This is the formdata", formdata);
  const teams = [formdata.cricket_team1, formdata.cricket_team2];

  return (
    <>
      {/* Description */}
      <div className="mb-6 -mt-2">
        <p className="text-sm text-gray-500 text-center">
          Manage the cricket match's winners here.
        </p>
      </div>

      {/* Match Info */}
      <div className="mb-8 border-t pt-6">
        <h2 className="text-xl font-semibold text-gray-900">
          #{formdata?.id} {formdata?.cricket_team1?.name} Vs{" "}
          {formdata?.cricket_team2?.name}
        </h2>
      </div>

      {/* Form */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Toss Winner */}
          <FormField
            control={form.control}
            name="toss_winner"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base text-gray-900">
                  Toss Winner
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  key={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="h-12 border border-gray-300 bg-white text-base">
                      <SelectValue placeholder="Select toss winner" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {teams.map((team: any) => (
                      <SelectItem key={team.id} value={team.id.toString()}>
                        <div className="flex items-center gap-2">
                          <CricketFlag
                            isoCode={team.iso_code}
                            className="w-5 h-5 shadow-sm rounded-sm"
                          />
                          <span>{team.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Match Winner */}
          <FormField
            control={form.control}
            name="match_winner"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base text-gray-900">
                  Match Winner
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  key={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="h-12 border border-gray-300 bg-white text-base">
                      <SelectValue placeholder="Select match winner" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {teams?.map((team: any) => (
                      <SelectItem key={team.id} value={team.id.toString()}>
                        <div className="flex items-center gap-2">
                          <CricketFlag
                            isoCode={team.iso_code}
                            className="w-5 h-5 shadow-sm rounded-sm"
                          />
                          <span>{team.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Player of Match */}
          <FormField
            control={form.control}
            name="player_of_the_match"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base text-gray-900">
                  Player of Match
                </FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="h-12 border border-gray-300 bg-white text-base">
                      <SelectValue placeholder="Select player of match" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {players?.map((player: any) => (
                      <SelectItem
                        key={player?.id}
                        value={player?.id?.toString()}
                      >
                        {player?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <Button
              type="submit"
              disabled={isLoading}
              className="h-14 w-full rounded-full bg-teal-500 px-8 text-lg font-semibold text-white hover:bg-teal-600 disabled:opacity-50 sm:w-auto sm:px-12"
            >
              {isLoading ? "Updating..." : "Update Winners"}
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
}
